import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideZoneChangeDetection } from '@angular/core';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Nora from '@primeuix/themes/nora';
import { NavMenuModule } from './modules/nav-menu/nav-menu.module';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { LOCALE_ID } from '@angular/core';

import { AppComponent } from './app.component';
import { ValidationServiceProvider } from "./services/validation.service";
import { DateTimeService, AccountService } from './services';
import { AppRoutingModule } from './app.routing.module';
import { LoaderModule } from './modules/loader/loader.module';
import { AuthenticationService, AuthGuardService, RoleGuardService, AuthInterceptor } from './services/security';
import { MessageService } from 'primeng/api';
import { DisplayService } from '@services/display.service';
import { AddCsrfHeaderInterceptorService } from './services/security/add-csrf-header-interceptor.service';
import { LoaderInterceptorService } from './services/loader/loader-interceptor.service';
import { WithCredentialsInterceptorService } from './services/security/with-credentials-interceptor.service';
import { AppFooterModule } from '@modules/footer/footer.module';
import { AppCommonModule } from '@modules/common/app-common.module';
import { AddAuditDataInterceptorService } from '@services/audit/add-audit-data-interceptor.service';
import { ErrorModule } from '@modules/error/error.module';
import { HomeModule } from './modules/home/home.module';
import { AuthAccountModule } from '@modules/account/login/auth.module';
import { CommonService } from './services/common.service';
// Parité V1 : DialogModule importé individuellement (le barrel 'primeng/primeng' n'existe plus en v21).
import { DialogModule } from 'primeng/dialog';
import { CommonPdfViewerComponent } from './modules/common/components/pdf-viewer/pdf-viewer.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

// Parité V1 : imports morts omis (0 usage vérifié dans les 274 templates — voir inventory T009) :
// AngularFontAwesomeModule, ShowHidePasswordModule, FontAwesomeModule/FaConfig/FaIconLibrary/library.add,
// CookieService (ngx-cookie-service). Les icônes viennent du CSS fontawesome-free chargé globalement.

registerLocaleData(localeFr, 'fr-FR');

// Le preset « Nova » n'existe pas dans @primeuix/themes (seuls aura/lara/material/nora sont livrés).
// Base retenue : Nora (style plat, bordé, compact — le plus proche du nova-light de PrimeNG 8),
// re-tokenisée avec les valeurs du thème nova-light de ClientApp (voir DIFF-005) :
// primaire #007ad9, bordures #c8c8c8, surfaces d'en-tête #f4f4f4, rayon 3px.
const NovaLightParity = definePreset(Nora, {
  primitive: {
    borderRadius: { none: '0', xs: '2px', sm: '2px', md: '3px', lg: '3px', xl: '6px' }
  },
  semantic: {
    primary: {
      50: '#f4fafe', 100: '#cae6fc', 200: '#a0d2fa', 300: '#75bef8',
      400: '#4baaf5', 500: '#2196f3', 600: '#007ad9', 700: '#116fbf',
      800: '#005b9f', 900: '#00497f', 950: '#003666'
    },
    colorScheme: {
      light: {
        primary: { color: '#007ad9', contrastColor: '#ffffff', hoverColor: '#116fbf', activeColor: '#005b9f' },
        surface: {
          0: '#ffffff', 50: '#fafafa', 100: '#f4f4f4', 200: '#eaeaea', 300: '#d8dae2',
          400: '#c8c8c8', 500: '#a6a6a6', 600: '#848484', 700: '#666666', 800: '#4a4a4a', 900: '#333333', 950: '#212121'
        }
      }
    }
  }
});

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppCommonModule,
    NavMenuModule,
    AppRoutingModule,
    AuthAccountModule,
    LoaderModule,
    AppFooterModule,
    ErrorModule,
    HomeModule,
    DialogModule,
    NgxExtendedPdfViewerModule
  ],
  declarations: [
    AppComponent,
    CommonPdfViewerComponent
  ],
  providers: [
    // Parité V1 : détection de changement zone-based sans coalescence d'événements (Angular 8 n'en avait pas).
    provideZoneChangeDetection(),
    // Parité V1 : BrowserAnimationsModule → provideAnimations() (équivalent Angular 21).
    provideAnimations(),
    // Parité V1 : HttpClientModule → provideHttpClient(withInterceptorsFromDi()) ; les intercepteurs
    // restent des classes, enregistrées dans le MÊME ORDRE que ClientApp (critique — voir inventory T011).
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      // cssLayer : les styles PrimeNG passent sous les CSS non-layerisés (styles.scss, primeng-v8-parity.css),
      // reproduisant l'ordre de cascade de ClientApp (theme.css chargé avant les surcharges globales).
      theme: {
        preset: NovaLightParity,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'primeng'
          }
        }
      },
      ripple: false,
      // Parité V1 : [locale]="fr" du p-calendar (input supprimé en v21) → traduction globale des dates.
      // firstDayOfWeek laissé par défaut (dimanche), comme en V1 où la clé "firstDay" était ignorée.
      translation: {
        dayNames: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
        dayNamesShort: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
        dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
        monthNames: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
        monthNamesShort: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
        today: "Aujourd'hui",
        clear: "Effacer"
      }
    }),
    ValidationServiceProvider,
    AuthenticationService,
    AuthGuardService,
    RoleGuardService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: AddCsrfHeaderInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WithCredentialsInterceptorService, multi: true },
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    { provide: HTTP_INTERCEPTORS, useClass: AddAuditDataInterceptorService, multi: true },

    DateTimeService,
    MessageService,
    DisplayService,
    AccountService,
    CommonService
  ],
  bootstrap: [AppComponent]
  // Parité V1 : entryComponents supprimé (concept disparu depuis Angular 13, sans équivalent nécessaire).
})
export class AppModule { }
